import { array, object, string, TestContext } from 'yup';

import { AuditAction, FormStatus } from '@assess/shared/ontology-types';

import { IAuditEntry } from '../entity/audit-entry.interface';
import { IProgramPeriodSettings } from '../entity/program-period/program-period-settings';
import { IReasonCode } from '../entity/reason-code.interface';

interface IConfig {
  reasonCodes: Record<string, any>;
  formStatusId: string;
  programPeriodSettings: IProgramPeriodSettings;
}

export const auditEntrySchemaFactory = (validateRejectReasonCodeIf: (config: any) => boolean) =>
  object({
    statusReasonCodeId: string().test(
      'statusReasonCodeId',
      'When status is approved or rejected this field is required',
      function (this: TestContext, value: any) {
        const context = this.options?.context as any;
        const config = context.config as IConfig;
        if (
          (config?.reasonCodes[config?.formStatusId]?.secondaryType === FormStatus.Approved ||
            config?.reasonCodes[config?.formStatusId]?.secondaryType === FormStatus.Rejected) &&
          value
        ) {
          return (
            config?.reasonCodes[value]?.secondaryType === AuditAction.Approved ||
            config?.reasonCodes[value]?.secondaryType === AuditAction.Rejected
          );
        }

        return true;
      }
    ),
    rejectReasonCodeId: string().when(['$config'], ([otherConfig], schema) => {
      return validateRejectReasonCodeIf(otherConfig)
        ? schema
            .nullable()
            .test('rejectReasonCodeId', 'Reason code is required', function (this: TestContext, value: any) {
              const context = this.options?.context as any;
              const config = context.config as IConfig;

              if (
                config?.reasonCodes[config?.formStatusId]?.secondaryType !== FormStatus.InReview &&
                config?.reasonCodes[this.parent?.statusReasonCodeId]?.secondaryType === AuditAction.Rejected
              ) {
                return !!value;
              }

              return true;
            })
        : schema.nullable();
    }),
  });

export const qualityAuditEntry = auditEntrySchemaFactory(
  (config: any) => config?.auditConfiguration?.qualityAuditReasonCodesEnabled
);
export const riskAuditEntry = auditEntrySchemaFactory(
  (config: any) => config?.auditConfiguration?.riskAuditReasonCodesEnabled
);

const formStatusReasonCode = string()
  .required()
  .test(
    'formStatusReasonCode',
    'Form cannot be returned if all entries have been approved',
    function (this: TestContext, value: string) {
      const context = this.options?.context as any;
      const config = context.config as IConfig;

      if (config.reasonCodes[value]?.secondaryType === FormStatus.Returned) {
        if (config.programPeriodSettings.userDefinedEnabled) {
          return true;
        }

        const {
          auditRiskEntries,
          auditQualityEntries,
        }: { auditRiskEntries: IAuditEntry[]; auditQualityEntries: IAuditEntry[] } = this.parent;

        return (
          notAllApproved(config.reasonCodes, auditRiskEntries) ||
          notAllApproved(config.reasonCodes, auditQualityEntries)
        );
      }

      return true;
    }
  );

const notAllApproved = (reasonCodes: Record<string, IReasonCode>, entries?: IAuditEntry[]) => {
  if (!entries || entries.length === 0) {
    return false;
  }

  return entries.some((e) => reasonCodes[e.statusReasonCodeId].secondaryType !== AuditAction.Approved);
};
export const auditEntrySchema = object({
  auditRiskEntries: array().of(riskAuditEntry),
  auditQualityEntries: array().of(qualityAuditEntry),
  formStatusReasonCode,
});
